import { Notebook, MindMapNode } from '../types';

/**
 * Calculate the size of a notebook in bytes
 * Uses TextEncoder to get accurate UTF-8 byte size
 */
export const getNotebookSize = (notebook: Notebook): number => {
    try {
        const jsonString = JSON.stringify(notebook);
        return new TextEncoder().encode(jsonString).length;
    } catch (error) {
        console.error('Failed to calculate notebook size:', error);
        return 0;
    }
};

/**
 * Calculate the size of a single node in bytes
 */
export const getNodeSize = (node: MindMapNode): number => {
    try {
        const jsonString = JSON.stringify(node);
        return new TextEncoder().encode(jsonString).length;
    } catch (error) {
        console.error('Failed to calculate node size:', error);
        return 0;
    }
};

/**
 * Get detailed breakdown of notebook size
 * Returns total size, top heaviest nodes, and average node size
 */
export const getNotebookSizeBreakdown = (notebook: Notebook) => {
    const totalSize = getNotebookSize(notebook);

    // Calculate size for each node
    const nodeSizes = notebook.nodes.map(node => ({
        id: node.id,
        title: node.title || 'Untitled',
        size: getNodeSize(node),
        hasImage: !!node.imageBase64,
        hasDrawing: !!node.drawingBase64,
        attachmentCount: node.attachments?.length || 0,
        hasAnalysis: !!node.analysis,
        reviewCount: node.reviewHistory?.length || 0
    }));

    // Sort by size (heaviest first)
    const sortedNodes = [...nodeSizes].sort((a, b) => b.size - a.size);

    // Calculate average
    const avgNodeSize = nodeSizes.length > 0
        ? nodeSizes.reduce((sum, n) => sum + n.size, 0) / nodeSizes.length
        : 0;

    // Calculate metadata size (total - all nodes)
    const nodesSize = nodeSizes.reduce((sum, n) => sum + n.size, 0);
    const metadataSize = totalSize - nodesSize;

    return {
        total: totalSize,
        totalKB: (totalSize / 1000).toFixed(2),
        totalMB: (totalSize / 1000000).toFixed(3),
        percentage: ((totalSize / 950000) * 100).toFixed(1), // % of 950KB limit
        nodeCount: notebook.nodes.length,
        avgNodeSize: avgNodeSize,
        avgNodeSizeKB: (avgNodeSize / 1000).toFixed(2),
        heaviestNodes: sortedNodes.slice(0, 10), // Top 10
        lightestNodes: sortedNodes.slice(-5).reverse(), // Bottom 5
        metadataSize: metadataSize,
        metadataSizeKB: (metadataSize / 1000).toFixed(2),
        nodesSize: nodesSize,
        nodesSizeKB: (nodesSize / 1000).toFixed(2),
        // Breakdown by type
        breakdown: {
            withImages: nodeSizes.filter(n => n.hasImage).length,
            withDrawings: nodeSizes.filter(n => n.hasDrawing).length,
            withAttachments: nodeSizes.filter(n => n.attachmentCount > 0).length,
            withAnalysis: nodeSizes.filter(n => n.hasAnalysis).length,
            withReviews: nodeSizes.filter(n => n.reviewCount > 0).length,
        }
    };
};

/**
 * Format bytes to human-readable string
 */
export const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1000;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Check if notebook is approaching size limit
 */
export const getSizeLevelWarning = (notebook: Notebook): {
    level: 'safe' | 'info' | 'warning' | 'danger';
    message: string;
    percentage: number;
} => {
    const size = getNotebookSize(notebook);
    const percentage = (size / 950000) * 100;

    if (percentage < 70) {
        return {
            level: 'safe',
            message: '',
            percentage
        };
    } else if (percentage < 80) {
        return {
            level: 'info',
            message: 'Notebook is becoming large',
            percentage
        };
    } else if (percentage < 90) {
        return {
            level: 'warning',
            message: 'Notebook is approaching size limit',
            percentage
        };
    } else {
        return {
            level: 'danger',
            message: 'Notebook is near maximum size! Consider splitting into multiple notebooks.',
            percentage
        };
    }
};
